import {
  getJpegDimensions,
  getPngDimensions
} from "components/pdf-generation-v3/utils";
import { getFileSizeError } from "utils/validations.utils";

/**
 * Validates file sizes for an array of photos
 * @param photos - Array of File objects to validate
 * @param maxSizeLimit - Maximum file size in bytes
 * @returns Error message if validation fails, null otherwise
 */
export function validatePhotoFileSizes(
  photos: File[],
  maxSizeLimit: number
): string | null {
  if (!photos?.length) {
    return null;
  }

  for (const photo of photos) {
    const error = getFileSizeError(photo.size, maxSizeLimit);
    if (error) {
      return error;
    }
  }

  return null;
}

/**
 * Validates image dimensions for a photo
 * @param photo - File object to validate
 * @param maxWidth - Maximum allowed width in pixels
 * @returns Promise that resolves to error message if validation fails, null otherwise
 */
export async function validatePhotoDimensions(
  photo: File,
  maxWidth: number
): Promise<string | null> {
  if (!photo || !maxWidth) {
    return null;
  }

  const fileType = photo.type.toLowerCase();
  const isJpeg = fileType === "image/jpeg" || fileType === "image/jpg";
  const isPng = fileType === "image/png";

  if (!isJpeg && !isPng) {
    return null;
  }

  try {
    const arrayBuffer = await photo.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const dimensions = isJpeg
      ? getJpegDimensions(uint8Array)
      : getPngDimensions(uint8Array);

    if (dimensions && dimensions.width > maxWidth) {
      return `Image width (${dimensions.width}px) exceeds maximum allowed width (${maxWidth}px)`;
    }

    return null;
  } catch (error) {
    console.error("Error reading image dimensions:", error);
    return null;
  }
}
